import moment from 'moment';
import { Typography } from 'antd';
import StatusTag from 'components/ui/base/status/StatusTag';
import { InvoicesStatuses } from 'store/invoices/types';
import { invoiceStatusColorMap, NO_DUE_INVOICE_STATUSES } from '../utils';

type Props = {
    status: InvoicesStatuses;
    dataTestId?: string;
    due?: {
        dueDaysCountForWarning: number;
        dueDate: string;
    };
};

const calculateDaysUntilDue = (dueDate: string) => {
    if (!dueDate) return null;
    const today = moment().startOf('day');
    const due = moment(dueDate).startOf('day');
    return due.diff(today, 'days');
};

const InvoiceStatus = ({ status, due, dataTestId }: Props) => {
    const renderStatusTag = () => {
        switch (status) {
            case InvoicesStatuses.DRAFT:
            case InvoicesStatuses.DRAFT_NON_EDITABLE:
                return <StatusTag color={invoiceStatusColorMap.DRAFT} status="Draft" data-testid={dataTestId} />;
            case InvoicesStatuses.AWAITING_PAYMENT:
                return (
                    <StatusTag
                        color={invoiceStatusColorMap.AWAITING_PAYMENT}
                        status="To Be Paid"
                        data-testid={dataTestId}
                    />
                );
            case InvoicesStatuses.OVERDUE:
                return <StatusTag color={invoiceStatusColorMap.OVERDUE} status="Overdue" data-testid={dataTestId} />;
            case InvoicesStatuses.PAID:
                return <StatusTag color="var(--polar-green-6)" status="Payment Received" data-testid={dataTestId} />;
            case InvoicesStatuses.PARTIALLY_PAID:
                return <StatusTag color="var(--polar-green-8)" status="Partially Paid" data-testid={dataTestId} />;
            case InvoicesStatuses.CANCELLED:
                return <StatusTag color="var(--dust-red-8)" status="Cancelled" data-testid={dataTestId} />;
            default:
                return <StatusTag color="var(--dust-red-6)" status="Unknown Status" data-testid={dataTestId} />;
        }
    };

    const shouldShowDueInfo = due && !NO_DUE_INVOICE_STATUSES.includes(status);

    if (!shouldShowDueInfo || !due) {
        return renderStatusTag();
    }

    const daysLeft = calculateDaysUntilDue(due.dueDate);
    let text: string | null = null;
    let color: string | undefined;

    if (daysLeft !== null) {
        if (daysLeft < 0 || status === InvoicesStatuses.OVERDUE) {
            text = `${Math.abs(daysLeft)} day${Math.abs(daysLeft) !== 1 ? 's' : ''} ago`;
            color = 'var(--dust-red-6)';
        } else if (daysLeft === 0) {
            text = 'Due today';
            color = 'var(--dust-red-6)';
        } else {
            text = `Due in ${daysLeft} day${daysLeft !== 1 ? 's' : ''}`;
            if (daysLeft <= due.dueDaysCountForWarning) {
                color = 'var(--dust-red-6)';
            }
        }
    }

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {renderStatusTag()}
            {text && <Typography.Text style={{ marginLeft: 22, color }}>{text}</Typography.Text>}
        </div>
    );
};

export default InvoiceStatus;
